import { Text, Spacer } from "@hackthenorth/north";
import { Button } from "north.js";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import Icon from "src/shared/components/Icon";
import { errorToast } from "src/shared/components/Toast";
import { Route } from "src/shared/constants/route";
import { useHackerContext } from "src/shared/contexts";
import { HackerStage } from "src/shared/contexts/HackerContext/types";
import { InfoIcon, WaiverDownload } from "src/static/icons";

import { SideCard, SideCardLabel } from "../home/SideCard";

import { DEFAULT_CUSTOMIZATION, STAMP_NAME_MAX_LENGTH } from "./constants";
import { downloadGraphic } from "./downloadGraphic";
import StampPreview from "./StampPreview";
import { useFirstName } from "./useFirstName";
import { useGraphicCustomization } from "./useGraphicCustomization";

/**
 * "Your Share Graphic" card shown on the RSVP status page, below the ticket.
 * Before the RSVP is confirmed, the card previews a name-seeded default
 * graphic behind a lock treatment. Once confirmed, Customize + Download
 * unlock.
 */
const ShareGraphicCard: React.FC = () => {
  const navigate = useNavigate();
  const firstName = useFirstName();
  const { stage } = useHackerContext();
  const { customization } = useGraphicCustomization();
  const [downloading, setDownloading] = useState(false);

  const isRsvpComplete =
    stage === HackerStage.CONFIRMED || stage === HackerStage.CHECKED_IN;
  const previewCustomization = useMemo(
    () =>
      isRsvpComplete
        ? customization
        : {
            ...DEFAULT_CUSTOMIZATION,
            stampName: firstName.slice(0, STAMP_NAME_MAX_LENGTH),
          },
    [customization, firstName, isRsvpComplete]
  );

  const onDownload = async () => {
    setDownloading(true);
    try {
      await downloadGraphic(customization, firstName);
    } catch {
      errorToast("Something went wrong generating your graphic — try again!");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <SideCard>
      <SideCardLabel>Your Share Graphic</SideCardLabel>
      <Spacer height={16} />
      <PreviewWrapper
        $locked={!isRsvpComplete}
        onContextMenu={(event) => {
          if (!isRsvpComplete) event.preventDefault();
        }}
      >
        <PreviewSurface $locked={!isRsvpComplete}>
          <StampPreview
            customization={previewCustomization}
            firstName={firstName}
          />
        </PreviewSurface>
        {!isRsvpComplete && (
          <PreviewLock aria-hidden>
            <LockBadge>
              <StyledLockIcon name="lock" size="20px" />
            </LockBadge>
          </PreviewLock>
        )}
      </PreviewWrapper>
      <Spacer height={16} />
      {isRsvpComplete ? (
        <>
          <Text mods="grey">
            Make it yours, then download it and tell everyone you&apos;re going.
          </Text>
          <Spacer height={16} />
          <ButtonRow>
            <Button
              color="primary"
              size="lg"
              onClick={() => navigate(Route.HACKER_GRAPHIC)}
            >
              Customize
            </Button>
            <Button
              color="secondary"
              size="lg"
              onClick={onDownload}
              disabled={downloading}
            >
              <ButtonContent>
                <WaiverDownload aria-hidden />
                {downloading ? "Preparing..." : "Download"}
              </ButtonContent>
            </Button>
          </ButtonRow>
        </>
      ) : (
        <InfoBanner role="status">
          <StyledInfoIcon aria-hidden />
          <InfoCopy>
            <InfoTitle>Complete your RSVP to unlock this graphic.</InfoTitle>
            <InfoDescription>
              Then customize and download your share graphic.
            </InfoDescription>
          </InfoCopy>
        </InfoBanner>
      )}
    </SideCard>
  );
};

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;

  > button {
    flex: 1;
  }
`;

const ButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const PreviewWrapper = styled.div<{ $locked: boolean }>`
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  cursor: ${({ $locked }) => ($locked ? "not-allowed" : "default")};
`;

const PreviewSurface = styled.div<{ $locked: boolean }>`
  opacity: ${({ $locked }) => ($locked ? 0.5 : 1)};
`;

const PreviewLock = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
`;

const LockBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 50%;
  background: ${({ theme }) => theme.globalConstants.color.white};
`;

const StyledLockIcon = styled(Icon)`
  fill: none;
  stroke: ${({ theme }) => theme.globalConstants.color.bluePrimary1};
`;

const InfoBanner = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border-radius: 8px;
  background: ${({ theme }) =>
    theme.globalConstants.color.backgroundPrimaryLight};
`;

const StyledInfoIcon = styled(InfoIcon)`
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  margin-top: 2px;
`;

const InfoCopy = styled.div`
  min-width: 0;
`;

const InfoTitle = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.globalConstants.color.brandPrimary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 14px;
  font-weight: 700;
  line-height: 1.4;
`;

const InfoDescription = styled.p`
  margin: 2px 0 0;
  color: ${({ theme }) => theme.globalConstants.color.textPrimary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
`;

export default ShareGraphicCard;
